/**
 * Links between files (`document-ai/0024`, Phase 4): the ones a person sees,
 * and answering or removing one. Same shape as `brain-api.ts`.
 *
 * Every key sits under `["project", id, "links"]`, so one invalidation after
 * an answer moves every screen that lists a link: the file's page, the review
 * and the debug view.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Containment, DocumentLink, SetLinkStateInput } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import { changeKeys } from "@/features/review/review-api";

export const linkKeys = {
  all: (projectId: string) => ["project", projectId, "links"] as const,
  document: (projectId: string, documentId: string) =>
    ["project", projectId, "links", "document", documentId] as const,
  asked: (projectId: string, importId: string) =>
    ["project", projectId, "links", "asked", importId] as const,
  /** Every link of the project waiting for an answer, whatever its import. */
  askedAll: (projectId: string) => ["project", projectId, "links", "asked-all"] as const,
};

/** One file's links, in and out, and what its container says (`containment`). */
export function useDocumentLinks(projectId: string | undefined, documentId: string | undefined) {
  return useQuery({
    queryKey: linkKeys.document(projectId ?? "", documentId ?? ""),
    enabled: Boolean(projectId && documentId),
    queryFn: async (): Promise<{ links: DocumentLink[]; containment: Containment | null }> => {
      const res = await api.projects.listLinks({
        params: { id: projectId! },
        query: { documentId: documentId! },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return { links: res.body.links, containment: res.body.containment };
    },
  });
}

/** The links one bundle's organising left for a person to answer. */
export function useAskedLinks(projectId: string | undefined, importId: string | undefined) {
  return useQuery({
    queryKey: linkKeys.asked(projectId ?? "", importId ?? ""),
    enabled: Boolean(projectId && importId),
    queryFn: async (): Promise<DocumentLink[]> => {
      const res = await api.projects.listLinks({
        params: { id: projectId! },
        query: { importId: importId!, state: "asked" },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.links;
    },
  });
}

/**
 * Every link of the project waiting for an answer, in one request
 * (2026-09-26, HID-04). The banner counts them per import from this, so an
 * import marked reviewed with a link still open keeps its line, and a project
 * with many imports makes one request, not one per import.
 */
export function useProjectAskedLinks(projectId: string | undefined) {
  return useQuery({
    queryKey: linkKeys.askedAll(projectId ?? ""),
    enabled: Boolean(projectId),
    queryFn: async (): Promise<DocumentLink[]> => {
      const res = await api.projects.listLinks({
        params: { id: projectId! },
        query: { state: "asked" },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.links;
    },
  });
}

/** Yes to an asked link (`live`), No to one, or removing a live one (`removed`). */
export function useSetLinkState(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { linkId: string } & SetLinkStateInput): Promise<DocumentLink> => {
      const res = await api.projects.setLinkState({
        params: { id: projectId, linkId: input.linkId },
        body: { state: input.state },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.link;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: linkKeys.all(projectId) });
      void queryClient.invalidateQueries({ queryKey: changeKeys.list(projectId) });
      void queryClient.invalidateQueries({ queryKey: ["project", projectId, "debug"] });
    },
  });
}
